import { AsyncPipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    viewChild,
    viewChildren,
    output,
    computed,
    linkedSignal,
} from '@angular/core';
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { Company, GoogleMapPoint, MAP_STYLES } from '@warema/shared/models';
import { GoogleApisScriptInjectorService } from '@warema/shared/services';
import { SharedSvgModule } from '@warema/shared/svg';

@Component({
    selector: 'warema-google-map',
    templateUrl: './google-map.component.html',
    styleUrls: ['./google-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, GoogleMap, MapMarker, MapInfoWindow, SharedSvgModule],
})
export class GoogleMapComponent implements AfterViewInit {
    public readonly googleApisScriptInjectorService = inject(GoogleApisScriptInjectorService);

    private readonly _googleMap = viewChild(GoogleMap);

    public readonly selectedCompany = input<Company | undefined>(undefined);

    public readonly companies = input<Array<Company>>([]);

    public readonly mapMarkerSelected = output<string>();

    private readonly _infoWindow = viewChild(MapInfoWindow);

    private readonly _markers = viewChildren(MapMarker);

    public readonly selectedCompanyValue = linkedSignal(() => this.selectedCompany());

    public readonly points = computed(() => {
        return this.companies().map(
            c =>
                ({
                    position: { lat: c.latitude, lng: c.longitude },
                    companyAddressId: c.addressId,
                }) as GoogleMapPoint,
        );
    });

    public readonly mapOptions: google.maps.MapOptions = {
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        center: new google.maps.LatLng(51, 11),
        zoom: 6,
        maxZoom: 12,
        streetViewControl: false,
        styles: MAP_STYLES,
        mapTypeControl: false,
    };

    constructor() {
        effect(() => {
            this._fitBounds();
        });

        effect(() => {
            const company = this.selectedCompany();
            if (company && company !== this.selectedCompanyValue()) {
                this.selectedCompanyValue.set(company);
                const markerIndex = this.points().findIndex(x => x.companyAddressId === company.addressId);
                this.markerSelected(markerIndex, company.addressId);
            }
        });
    }

    public ngAfterViewInit(): void {
        this._fitBounds();
    }

    public markerSelected(windowIndex: number, companyAddressId: string): void {
        this.mapMarkerSelected.emit(companyAddressId);
        this._markers()?.forEach((marker: MapMarker, ix: number) => {
            if (windowIndex === ix) {
                this._infoWindow()?.open(marker);
            }
        });
    }

    private _fitBounds(): void {
        if (this.points().length === 0) {
            return;
        }
        const markers = this.points().map(x => ({
            lat: x.position?.lat,
            lng: x.position?.lng,
        }));

        const bounds = new google.maps.LatLngBounds();

        markers.forEach((marker: { lat: number | undefined; lng: number | undefined }) => {
            bounds.extend(new google.maps.LatLng(Number(marker?.lat), Number(marker?.lng)));
        });
        this._googleMap()?.fitBounds(bounds);
    }
}
